import { Alert, Button, Modal, Select, Space, Tag, Tooltip, Typography } from "antd";
import React, { useEffect, useState } from "react";
import type { RefundRow, RefundStatus } from "../../../cash-log/refunds.api";
import { AddBankAccountModal } from "../../returns/components/refund_modal";
import { BANK_CHOICES, UserBankDetails, listUserBankDetails } from "../../returns/returns.api";

const muted: React.CSSProperties = { color: "#8c8c8c", fontSize: 12 };
const num: React.CSSProperties = { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const twoLine: React.CSSProperties = { lineHeight: "20px", minWidth: 0 };

export const isOpenRefund = (s: RefundStatus) =>
  s === "pending" || s === "partial" || s === "partially_settled";

export const needsBank = (row: RefundRow) =>
  isOpenRefund(row.status) &&
  (!row.destination?.bank_details || row.destination?.bank_source === "on_file");

const formatRupees = (v: string | number) =>
  `₹${Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const CopyText: React.FC<{ value: string; label?: string }> = ({ value, label }) => (
  <Typography.Text
    style={{ ...num, fontSize: 13 }}
    copyable={{ text: value, tooltips: [`Copy ${label || ""}`.trim(), "Copied"] }}
  >
    {value}
  </Typography.Text>
);

export const RefundPayToCell: React.FC<{ row: RefundRow; onAttach?: () => void }> = ({
  row,
  onAttach,
}) => {
  const d = row.destination;
  const bank = d?.bank_details;
  const canAttach = !!onAttach && isOpenRefund(row.status);
  if (!bank) {
    const several = (d?.accounts_on_file || 0) > 1;
    return (
      <Tooltip
        title={
          several
            ? "No account was chosen for this refund and the customer has several on file. Confirm which one to pay."
            : "No bank account on file for this customer. It can't be paid out until one is added."
        }
      >
        <Space size={6}>
          <Tag color="red" style={{ marginInlineEnd: 0 }}>
            {several ? `${d?.accounts_on_file} accounts on file` : "Bank details missing"}
          </Tag>
          {canAttach && (
            <Button size="small" type="link" style={{ padding: 0 }} onClick={onAttach}>
              {several ? "Pick account" : "Add bank"}
            </Button>
          )}
        </Space>
      </Tooltip>
    );
  }
  return (
    <div style={twoLine}>
      <div style={{ display: "flex", gap: 6, alignItems: "baseline", minWidth: 0 }}>
        <Typography.Text strong ellipsis={{ tooltip: bank.account_holder_name }} style={{ maxWidth: 180 }}>
          {bank.account_holder_name}
        </Typography.Text>
        <Typography.Text type="secondary" ellipsis style={{ fontSize: 12, maxWidth: 140 }}>
          {bank.bank_label || bank.bank_name}
        </Typography.Text>
        {bank.is_active === false && (
          <Tag color="red" style={{ marginInlineEnd: 0 }}>
            inactive
          </Tag>
        )}
        {d?.bank_source === "on_file" && (
          <Tooltip title="No account was chosen for this refund. This is the customer's only active account on file — confirm with the customer, then click to attach it.">
            <Tag
              color="gold"
              style={{ marginInlineEnd: 0, cursor: canAttach ? "pointer" : undefined }}
              onClick={canAttach ? onAttach : undefined}
            >
              on file · confirm
            </Tag>
          </Tooltip>
        )}
      </div>
      <Space size={12} style={{ whiteSpace: "nowrap" }}>
        <CopyText value={bank.account_number} label="account number" />
        <CopyText value={bank.ifsc} label="IFSC" />
      </Space>
    </div>
  );
};

export const AttachBankModal: React.FC<{
  row: RefundRow | null;
  attach: (row: RefundRow, bankDetailsId: number) => Promise<RefundRow | null>;
  onClose: () => void;
  onDone: (row: RefundRow) => void;
}> = ({ row, attach, onClose, onDone }) => {
  const [banks, setBanks] = useState<UserBankDetails[]>([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected(null);
    setBanks([]);
    if (!row?.client_id) return;
    setLoadingBanks(true);
    listUserBankDetails(row.client_id).then((list) => {
      const active = list.filter((b) => b.is_active !== false);
      setBanks(active);
      if (active.length === 1) setSelected(active[0].id);
      setLoadingBanks(false);
    });
  }, [row]);

  const submit = async () => {
    if (!row || !selected) return;
    setSaving(true);
    const updated = await attach(row, selected);
    setSaving(false);
    if (updated) {
      onDone(updated);
      onClose();
    }
  };

  const bankLabel = (value: string) =>
    BANK_CHOICES.find((c) => c.value === value)?.label || value;

  return (
    <>
      <Modal
        open={!!row}
        title={row ? `Bank account for refund #${row.id}` : ""}
        okText="Attach"
        okButtonProps={{ disabled: !selected }}
        confirmLoading={saving}
        onOk={submit}
        onCancel={onClose}
        destroyOnClose
      >
        {row && (
          <>
            <div style={{ marginBottom: 12 }}>
              <Typography.Text strong>{row.customer_name || "Customer"}</Typography.Text>
              <span style={muted}> · {formatRupees(row.payout_rupees)} to pay</span>
            </div>
            {!row.client_id ? (
              <Alert type="error" showIcon message="This refund has no customer on record." />
            ) : (
              <Select
                style={{ width: "100%" }}
                loading={loadingBanks}
                placeholder={loadingBanks ? "Loading accounts…" : "Select a bank account"}
                value={selected ?? undefined}
                onChange={(v) => setSelected(v as number)}
                notFoundContent={loadingBanks ? null : "No bank account on file — add one below"}
                options={banks.map((b) => ({
                  value: b.id,
                  label: `${bankLabel(b.bank_name)} — ${b.account_holder_name} (…${b.account_number.slice(-4)} · ${b.ifsc})`,
                }))}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div style={{ padding: 8, borderTop: "1px solid #f0f0f0" }}>
                      <Button type="link" size="small" onClick={() => setAddOpen(true)}>
                        + Add new bank account
                      </Button>
                    </div>
                  </>
                )}
              />
            )}
            {row.client_id && !loadingBanks && banks.length === 0 && (
              <Button style={{ marginTop: 12 }} onClick={() => setAddOpen(true)}>
                + Add bank account
              </Button>
            )}
          </>
        )}
      </Modal>
      <AddBankAccountModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        userId={row?.client_id || undefined}
        onAdded={(bank) => {
          setBanks((prev) => [bank, ...prev.filter((b) => b.id !== bank.id)]);
          setSelected(bank.id);
          setAddOpen(false);
        }}
      />
    </>
  );
};
